import React, { useRef } from 'react';
import { Container, Paper, Box, Button, CircularProgress, Backdrop } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCaregiverForm } from '../../hooks/useCaregiverForm';
import NavStatusBar from '../../components/NavStatusBar';
import { STEP_LABELS } from './types';
import { SIGNATURE_STEP, TOTAL_STEPS } from './constants';
import DemographicsStep from './steps/DemographicsStep';
import HumanResourcesStep from './steps/HumanResourcesStep';
import EmergencyReferencesStep from './steps/EmergencyReferencesStep';
import AvailabilityStep from './steps/AvailabilityStep';
import MedicalStep from './steps/MedicalStep';
import SignatureStep, { SignatureStepHandle } from './steps/SignatureStep';
import SubmittedStep from './steps/SubmittedStep';

const CaregiverForm: React.FC = () => {
  const { t } = useTranslation();
  const signatureRef = useRef<SignatureStepHandle>(null);
  const {
    formData,
    fieldErrors,
    currentStep,
    isLoading,
    isSaving,
    isSubmitted,
    isGeneratingDocs,
    generatedDocs,
    docGenError,
    handleChange,
    handleCheckboxChange,
    handleMultiSelectChange,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    submitForm,
    setFormData,
  } = useCaregiverForm();

  const renderStep = () => {
    const commonProps = {
      formData,
      fieldErrors,
      handleChange,
      handleCheckboxChange,
      handleMultiSelectChange,
    };

    switch (currentStep) {
      case 1:
        return <DemographicsStep {...commonProps} setFormData={setFormData} />;
      case 2:
        return <HumanResourcesStep {...commonProps} />;
      case 3:
        return <EmergencyReferencesStep {...commonProps} />;
      case 4:
        return <AvailabilityStep {...commonProps} />;
      case 5:
        return <MedicalStep {...commonProps} />;
      case SIGNATURE_STEP:
        return (
          <SignatureStep
            ref={signatureRef}
            formData={formData}
            fieldErrors={fieldErrors}
            setFormData={setFormData}
          />
        );
      case TOTAL_STEPS:
        return (
          <SubmittedStep
            formData={formData}
            isGeneratingDocs={isGeneratingDocs}
            generatedDocs={generatedDocs}
            docGenError={docGenError}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mb: 4 }}>
      <Paper elevation={3} sx={{ my: 2, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mt: '1rem', p: '0.25rem' }}>
          <Box sx={{ mb: '10px' }}>
            <NavStatusBar
              steps={STEP_LABELS}
              currentStep={currentStep}
              onStepClick={goToStep}
            />
          </Box>

          <Box
            sx={{
              m: { xs: 0.5, sm: 1 },
              ml: { xs: 1 },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {renderStep()}
          </Box>

          {!isSubmitted && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              {currentStep === SIGNATURE_STEP && (
                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => signatureRef.current?.clearSignature()}
                    sx={{ mr: 1, marginLeft: { xs: 2, sm: 0 } }}
                  >
                    {t('form.clearSignature', 'Clear Signature')}
                  </Button>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexGrow: currentStep === SIGNATURE_STEP ? 0 : 1,
                  justifyContent: 'flex-end',
                  paddingRight: 1,
                  paddingLeft: 1,
                }}
              >
                {currentStep > 1 && (
                  <Button onClick={goToPreviousStep} sx={{ mr: 1 }}>
                    {t('form.back', 'Back')}
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={currentStep === SIGNATURE_STEP ? submitForm : goToNextStep}
                  disabled={isSaving}
                >
                  {isSaving
                    ? t('form.saving', 'Saving...')
                    : currentStep === SIGNATURE_STEP
                      ? t('form.submit', 'Submit')
                      : t('form.next', 'Next')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default CaregiverForm;
